/**
 * 付款對話框組件
 * 處理現金付款和 Line Pay
 * 找零與收款換算使用 currency.js 避免浮點誤差（同數字卻要找零）
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, SmartphoneNfc, Banknote, QrCode } from 'lucide-react';
import currency from 'currency.js';
import { formatTwdFromCents } from '@pos/shared/money';

const EPSILON_YUAN = 0.01; // 小於 0.01 元（1 分）視為 0，防止浮點殘差

/**
 * 應付（分）與收款輸入（字串，元）用 currency.js 算找零（分）。
 * - 收款必須傳 input 原始字串，不要先 Number() 再傳，避免污染。
 * - 找零若 < 0.01 元則歸零（Epsilon 防禦）。
 */
function computeChangeCents(
  totalAmountCents: number,
  paidYuanString: string
): number {
  const totalCur = currency(totalAmountCents, { fromCents: true });
  const paidCur = currency(paidYuanString.trim() || '0');
  const changeCur = paidCur.subtract(totalCur);
  const changeYuan = changeCur.value < 0 ? 0 : changeCur.value;
  if (changeYuan < EPSILON_YUAN) return 0;
  return Math.round(currency(changeYuan).multiply(100).value);
}

/** 收款字串（元）→ 分，只用 currency 與字串，不經 parseFloat */
function paidYuanStringToCents(paidYuanString: string): number {
  const paidCur = currency(paidYuanString.trim() || '0');
  return Math.round(paidCur.multiply(100).value);
}

/**
 * 付款方式
 */
export type PaymentMethod = 'cash' | 'linepay' | 'twqr';

/**
 * 付款對話框 Props
 */
export interface PaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: (paymentData: {
    method: PaymentMethod;
    paidAmount: number;
    changeAmount?: number;
  }) => void | Promise<void>;
  totalAmount: number;
}

/**
 * 付款對話框組件
 */
export function PaymentDialog({
  isOpen,
  onClose,
  onPaymentComplete,
  totalAmount,
}: PaymentDialogProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [cashAmount, setCashAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  /** 阻擋重複點擊「確認付款」，在 setState 生效前就擋掉第二次請求 */
  const submittingRef = useRef(false);

  /**
   * 重置表單
   */
  const resetForm = () => {
    setPaymentMethod('cash');
    setCashAmount('');
    setIsProcessing(false);
    submittingRef.current = false;
  };

  /**
   * 關閉對話框
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Esc 鍵關閉對話框
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetForm();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  /**
   * 處理付款完成
   * totalAmount 為「分」（cents）；收款金額輸入為「元」，需轉成「分」再比較與回傳
   * 使用 ref 阻擋重複點擊，並 await 父層建立訂單完成後才重置按鈕狀態，避免產生兩張訂單。
   */
  const handlePaymentComplete = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsProcessing(true);

    try {
      if (paymentMethod === 'cash') {
        const totalCents = Math.round(Number(totalAmount));
        const dueYuanStr = String(Math.round(totalCents / 100));
        const paidCents = paidYuanStringToCents(cashAmount);
        const rawChange = computeChangeCents(totalCents, cashAmount);
        const changeCents =
          cashAmount.trim() === dueYuanStr && paidCents >= totalCents
            ? 0
            : rawChange < 100
              ? 0
              : rawChange;

        if (paidCents < totalCents) {
          alert(
            `付款金額不足，還需要 ${formatTwdFromCents(totalCents - paidCents)}`
          );
          return;
        }

        const result = onPaymentComplete({
          method: 'cash',
          paidAmount: paidCents,
          changeAmount: changeCents,
        });
        if (
          result != null &&
          typeof (result as Promise<unknown>).then === 'function'
        ) {
          await result;
        }
      } else if (paymentMethod === 'linepay') {
        // Line Pay - 模擬處理時間
        await new Promise(resolve => setTimeout(resolve, 2000));
        const result = onPaymentComplete({
          method: 'linepay',
          paidAmount: totalAmount,
        });
        if (
          result != null &&
          typeof (result as Promise<unknown>).then === 'function'
        ) {
          await result;
        }
      } else {
        // TWQR - 模擬處理時間
        await new Promise(resolve => setTimeout(resolve, 2000));
        const result = onPaymentComplete({
          method: 'twqr',
          paidAmount: totalAmount,
        });
        if (
          result != null &&
          typeof (result as Promise<unknown>).then === 'function'
        ) {
          await result;
        }
      }
    } catch (error) {
      console.error('付款處理失敗:', error);
      alert('付款處理失敗，請重試');
    } finally {
      setIsProcessing(false);
      submittingRef.current = false;
    }
  };

  /**
   * 處理「免找」按鈕點擊：應付為「分」，收款欄位為「元」，填入整數元（避免浮點顯示）
   */
  const handleExactAmount = () => {
    setCashAmount(String(Math.round(totalAmountCents / 100)));
  };

  /**
   * 應付為整數分；收款用 input 原始字串（不經 parseFloat）交給 currency.js，避免污染。
   * 找零 < 0.01 元視為 0（Epsilon）；當「應付（元）」與「收款（元）」字串一致時強制找零 0（免找防呆）。
   */
  // 應付一律為整數分（Cart/ConfirmDialog 傳入的 totalPayableCents）
  const totalAmountCents = Math.round(Number(totalAmount));
  const dueYuanStr = String(Math.round(totalAmountCents / 100));
  const paidAmountCents = paidYuanStringToCents(cashAmount);
  const rawChangeCents = computeChangeCents(totalAmountCents, cashAmount);
  // 找零 < 100（1 元）視為 0，避免殘差進位造成差 1
  const changeAmountCents =
    cashAmount.trim() === dueYuanStr && paidAmountCents >= totalAmountCents
      ? 0
      : rawChangeCents < 100
        ? 0
        : rawChangeCents;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* 標題 */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">付款</h2>
            <p className="text-sm text-gray-600 mt-1">
              應付金額：
              <span className="font-semibold text-green-600">
                {formatTwdFromCents(totalAmountCents)}
              </span>
            </p>
          </div>
        </div>

        <div className="p-6">
          {/* 付款方式選擇 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              選擇付款方式
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 border-2 rounded-lg text-center transition-colors ${
                  paymentMethod === 'cash'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Banknote className="w-8 h-8 mx-auto mb-2" />
                <p className="font-medium">現金</p>
              </button>

              <button
                onClick={() => setPaymentMethod('linepay')}
                className={`p-4 border-2 rounded-lg text-center transition-colors ${
                  paymentMethod === 'linepay'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <SmartphoneNfc className="w-8 h-8 mx-auto mb-2" />
                <p className="font-medium">Line Pay</p>
              </button>

              <button
                onClick={() => setPaymentMethod('twqr')}
                className={`p-4 border-2 rounded-lg text-center transition-colors ${
                  paymentMethod === 'twqr'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <QrCode className="w-8 h-8 mx-auto mb-2" />
                <p className="font-medium">TWQR</p>
              </button>
            </div>
          </div>

          {/* 現金付款 */}
          {paymentMethod === 'cash' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  收款金額
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={cashAmount}
                    onChange={e => setCashAmount(e.target.value)}
                    placeholder="請輸入收款金額"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="1"
                  />
                  <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                    元
                  </span>
                  <button
                    type="button"
                    onClick={handleExactAmount}
                    className="px-3 py-2 text-sm font-medium bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    免找
                  </button>
                </div>
              </div>

              {/* 找零顯示（僅在需找零時顯示；同金額時為 0 不顯示） */}
              {paidAmountCents > 0 && changeAmountCents > 0 && (
                <div className="bg-blue-50 p-4 rounded-md">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">找零</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatTwdFromCents(changeAmountCents)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 按鈕 */}
        <div className="p-6 border-t border-gray-200 flex space-x-3">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            onClick={handlePaymentComplete}
            disabled={
              isProcessing ||
              (paymentMethod === 'cash' &&
                (paidAmountCents < totalAmountCents || paidAmountCents <= 0))
            }
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Loader2
                  className="inline-block w-4 h-4 mr-1.5 animate-spin"
                  aria-hidden
                />
                處理中...
              </>
            ) : (
              '確認付款'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
